<template>
    <canvas class="canvas-animation" :width="width" :height="height"/>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import { scrollTop } from 'bianco.viewport';
    import isDesktopMixin from 'framework/mixins/is-desktop';
    import isWebglAvailable from 'util/misc/is-webgl-available';
    import { add as addEvent, remove as removeEvent } from 'bianco.events';

    export default {
        mixins: [isDesktopMixin],
        computed: {
            ...mapGetters('animation-hooks', ['activeHook']),
            ...mapState('animation-hooks', ['hooks']),
            ...mapState('viewport', ['width', 'height', 'hidden']),
            currentHook() {
                return this.isDesktop ? this.activeHook : null;
            },
        },
        methods: {
            getCurrentHookOffset() {
                return this.currentHook ? this.currentHook.offset : this.hooks[0].offset;
            },
            getCurrentRotation() {
                return this.currentHook ? this.currentHook.rotation : this.hooks[0].rotation;
            },
            getCurrentPosition() {
                return this.currentHook ? this.currentHook.position : this.hooks[0].position;
            },
            bootApp() {
                if (!isWebglAvailable()) {
                    return;
                }

                import(
                    /* webpackPrefetch: true */
                    /* webpackChunkName: 'canvas-animation' */
                    './app'
                    ).then(app => {
                    this.app = app.default;
                    this.app
                        .init({
                            canvas: this.$el,
                            canScroll: this.isDesktop,
                            rotation: this.getCurrentRotation(),
                            offset: this.getCurrentHookOffset(),
                            position: this.getCurrentPosition(),
                        })
                        .setCameraPosition(this.scrollPosition)
                        .animate();

                    this.onScroll();
                    this.setListeners();
                });
            },
            setListeners() {
                this.$watch('width', this.onResize);
                this.$watch('height', this.onResize);
                this.$watch('activeHook', this.refreshOffsets);
                this.$watch('hidden', this.onVisibilityChange);
                this.$watch('isDesktop', isDesktop => this.app.setCanScroll(isDesktop));

                addEvent(window, 'scroll', this.onScroll);
            },
            refreshOffsets() {
                if (!this.activeHook || !this.isDesktop) {
                    return;
                }

                this.app
                    .setOffset(this.getCurrentHookOffset())
                    .setParticlePosition(this.getCurrentRotation(), this.getCurrentPosition());
            },
            onResize() {
                this.app
                    .setCurrentScroll(scrollTop())
                    .setOffset(this.getCurrentHookOffset())
                    .setParticlePosition(this.getCurrentRotation(), this.getCurrentPosition())
                    .setSize({ width: this.width, height: this.height });
            },
            onScroll() {
                this.app.setCurrentScroll(scrollTop());
            },
            onVisibilityChange() {
                this.app[this.hidden ? 'pause' : 'resume']();
            },
        },
        mounted() {
            const unwatch = this.$watch('hooks', () => {
                this.bootApp();
                unwatch();
            });
        },
        beforeDestroy() {
            if (this.app) {
                removeEvent(window, 'scroll', this.onScroll);
                this.app.destroy();
            }
        },
    };

</script>
